import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { AI_SKILLS_CATALOG } from "./aiSkillsCatalog";
import "./ai-skills.css";

marked.setOptions({ breaks: true, gfm: true });

export const AiSkills = () => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState("overview");
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selected = useMemo(
    () => AI_SKILLS_CATALOG.find((item) => item.id === selectedId) || AI_SKILLS_CATALOG[0],
    [selectedId]
  );

  const loadDoc = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    const base = process.env.PUBLIC_URL || "";
    const url = path.startsWith("/") ? `${base}${path}` : path;
    try {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const text = await response.text();
      if (text.trimStart().startsWith("<!DOCTYPE") || text.trimStart().startsWith("<html")) {
        throw new Error("not_found");
      }
      setMarkdown(text);
    } catch (e) {
      setMarkdown("");
      setError(e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = `${t("ai_skills.page_title")} | Promang`;
  }, [t]);

  useEffect(() => {
    if (selected?.path) {
      loadDoc(selected.path);
    }
  }, [selected?.path, loadDoc]);

  const html = useMemo(() => {
    if (!markdown) return "";
    return marked.parse(markdown);
  }, [markdown]);

  const onContentClick = useCallback((event) => {
    const anchor = event.target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("http") || href.startsWith("#")) return;
    if (!href.startsWith("/docs/")) return;
    event.preventDefault();
    const normalized = href.startsWith("/") ? href : `/${href}`;
    const catalogMatch = AI_SKILLS_CATALOG.find((item) => item.path === normalized);
    if (catalogMatch) {
      setSelectedId(catalogMatch.id);
      return;
    }
    if (normalized.endsWith("skills.md") && normalized.includes("/docs/ai")) {
      setSelectedId("overview");
      return;
    }
    const base = process.env.PUBLIC_URL || "";
    window.open(`${base}${normalized}`, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-violet-800">
              <SparklesIcon className="h-4 w-4" aria-hidden />
              {t("ai_skills.badge")}
            </div>
            <h1 className="text-2xl font-semibold text-slate-950">{t("ai_skills.page_title")}</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">{t("ai_skills.subtitle")}</p>
          </div>
          <Link
            to="/dashboard/guide"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {t("ai_skills.back_to_user_guide")}
          </Link>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl min-h-0 flex-1 flex-col gap-0 px-4 py-4 sm:flex-row sm:px-6">
        <nav
          className="mb-4 w-full shrink-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:mb-0 sm:mr-4 sm:w-64"
          aria-label={t("ai_skills.nav_label")}
        >
          <ul className="space-y-0.5">
            {AI_SKILLS_CATALOG.map((item) => {
              const active = item.id === selectedId;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                      active
                        ? "bg-violet-50 font-semibold text-violet-900"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {item.skill ? (
                      <span
                        className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-800"
                        title={t("ai_skills.skill_badge_hint")}
                      >
                        {t("ai_skills.skill_badge")}
                      </span>
                    ) : null}
                    <span className="min-w-0 truncate">{t(item.titleKey)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className="min-h-[320px] min-w-0 flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-slate-500">
              {t("ai_skills.loading")}
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">
              {t("ai_skills.load_error")} ({error})
            </div>
          ) : (
            <article
              className="ai-skills-prose max-w-none overflow-y-auto px-6 py-8"
              onClick={onContentClick}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
        </main>
      </div>
    </div>
  );
};
